import { apiFetch } from '../utils/apiClient';

export type DeliveryPaymentMethodType =
    | 'CASH'
    | 'POS'
    | 'PAYPAL'
    | 'STRIPE'
    | 'BANK_TRANSFER'
    | 'CUSTOM';

export interface DeliveryPaymentMethod {
    id: number | null;
    type: DeliveryPaymentMethodType;
    maxOrderTotal: number | null;
    active: boolean;
    allowChange: boolean;
    instructions?: string | null;
    displayName?: string | null;
    sortOrder?: number | null;
}

export interface DeliverySettings {
    enabled: boolean;
    deliveryFeatureEnabled: boolean;
    deliveryPaymentFeatureEnabled: boolean;
    deliveryGuestEnabled: boolean;
    suspensionMessage?: string | null;
    flatFee: number;
    currency: string;
    minOrderAmount: number | null;
    maxOrderAmount: number | null;
    maxDistanceKm: number | null;
    estimatedDeliveryMinutes: number | null;
    allowGuestCheckout: boolean;
    restaurantLatitude: number | null;
    restaurantLongitude: number | null;
    paymentMethods: DeliveryPaymentMethod[];
}

export interface DeliveryAddressPayload {
    fullName: string;
    phoneNumber: string;
    street: string;
    city: string;
    postalCode: string;
    country?: string;
    additionalInfo?: string;
    doorbell?: string;
    latitude?: number | null;
    longitude?: number | null;
}

export interface DeliveryOrderItemPayload {
    productId: string;
    productName: string;
    variantName?: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    notes?: string;
}

export type DeliveryOrderStatus =
    | 'NEW'
    | 'CONFIRMED'
    | 'PREPARING'
    | 'OUT_FOR_DELIVERY'
    | 'COMPLETED'
    | 'CANCELLED';

export type DeliveryPaymentStatus =
    | 'PENDING'
    | 'AUTHORIZED'
    | 'PAID'
    | 'REFUNDED'
    | 'CANCELLED';

export interface DeliveryOrderResponse {
    id: number;
    orderCode: string;
    status: DeliveryOrderStatus;
    paymentStatus: DeliveryPaymentStatus;
    paymentMethod: DeliveryPaymentMethodType | null;
    paypalOrderId?: string | null;
    paypalCaptureId?: string | null;
    customerName?: string | null;
    customerEmail?: string | null;
    customerPhone?: string | null;
    guestCheckout: boolean;
    userId?: number | null;
    notes?: string | null;
    subtotal: number;
    deliveryFee: number;
    total: number;
    estimatedDeliveryMinutes: number | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    paymentConfirmedAt?: string | null;
}

export interface DeliveryOrderRequestPayload {
    customerName: string;
    customerEmail?: string;
    customerPhone: string;
    guestCheckout: boolean;
    userId?: number;
    notes?: string;
    paymentMethod?: DeliveryPaymentMethodType | null;
    paypalOrderId?: string;
    subtotal: number;
    deliveryFee: number;
    total: number;
    address: DeliveryAddressPayload;
    items: DeliveryOrderItemPayload[];
}

const ensureNumber = (value: unknown): number | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const mapSettings = (raw: any): DeliverySettings => {
    const paymentMethods: DeliveryPaymentMethod[] = Array.isArray(raw?.paymentMethods)
        ? raw.paymentMethods
            .map((method: any) => ({
                id: ensureNumber(method?.id) ?? null,
                type: (method?.type ?? 'CASH') as DeliveryPaymentMethodType,
                maxOrderTotal: ensureNumber(method?.maxOrderTotal),
                active: Boolean(method?.active),
                allowChange: Boolean(method?.allowChange),
                instructions: method?.instructions ?? null,
                displayName: method?.displayName ?? null,
                sortOrder: ensureNumber(method?.sortOrder),
            }))
            .sort((a: DeliveryPaymentMethod, b: DeliveryPaymentMethod) => {
                const left = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
                const right = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
                if (left === right) return 0;
                return left < right ? -1 : 1;
            })
        : [];

    return {
        enabled: Boolean(raw?.enabled ?? raw?.deliveryFeatureEnabled ?? false),
        deliveryFeatureEnabled: Boolean(raw?.deliveryFeatureEnabled ?? raw?.enabled ?? false),
        deliveryPaymentFeatureEnabled: Boolean(raw?.deliveryPaymentFeatureEnabled),
        deliveryGuestEnabled: Boolean(raw?.deliveryGuestEnabled ?? raw?.allowGuestCheckout ?? true),
        suspensionMessage: raw?.suspensionMessage ?? null,
        flatFee: ensureNumber(raw?.flatFee) ?? 0,
        currency: typeof raw?.currency === 'string' && raw.currency.trim() ? raw.currency : 'EUR',
        minOrderAmount: ensureNumber(raw?.minOrderAmount),
        maxOrderAmount: ensureNumber(raw?.maxOrderAmount),
        maxDistanceKm: ensureNumber(raw?.maxDistanceKm),
        estimatedDeliveryMinutes: ensureNumber(raw?.estimatedDeliveryMinutes),
        allowGuestCheckout: Boolean(raw?.allowGuestCheckout ?? raw?.deliveryGuestEnabled ?? true),
        restaurantLatitude: ensureNumber(raw?.restaurantLatitude),
        restaurantLongitude: ensureNumber(raw?.restaurantLongitude),
        paymentMethods,
    };
};

const mapOrderResponse = (raw: any): DeliveryOrderResponse => ({
    id: ensureNumber(raw?.id) ?? 0,
    orderCode: typeof raw?.orderCode === 'string' ? raw.orderCode : '',
    status: (raw?.status ?? 'NEW') as DeliveryOrderStatus,
    paymentStatus: (raw?.paymentStatus ?? 'PENDING') as DeliveryPaymentStatus,
    paymentMethod: raw?.paymentMethod ?? null,
    paypalOrderId: raw?.paypalOrderId ?? null,
    paypalCaptureId: raw?.paypalCaptureId ?? null,
    customerName: raw?.customerName ?? null,
    customerEmail: raw?.customerEmail ?? null,
    customerPhone: raw?.customerPhone ?? null,
    guestCheckout: Boolean(raw?.guestCheckout),
    userId: ensureNumber(raw?.userId),
    notes: raw?.notes ?? null,
    subtotal: ensureNumber(raw?.subtotal) ?? 0,
    deliveryFee: ensureNumber(raw?.deliveryFee) ?? 0,
    total: ensureNumber(raw?.total) ?? 0,
    estimatedDeliveryMinutes: ensureNumber(raw?.estimatedDeliveryMinutes),
    createdAt: typeof raw?.createdAt === 'string' ? raw.createdAt : null,
    updatedAt: typeof raw?.updatedAt === 'string' ? raw.updatedAt : null,
    paymentConfirmedAt: typeof raw?.paymentConfirmedAt === 'string' ? raw.paymentConfirmedAt : null,
});

const readErrorMessage = async (res: Response): Promise<string> => {
    try {
        const text = await res.text();
        if (!text) return `HTTP ${res.status}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed && typeof parsed === 'object') {
                if (typeof parsed.message === 'string') return parsed.message;
                if (typeof parsed.error === 'string') return parsed.error;
            }
            return text;
        } catch {
            return text;
        }
    } catch {
        return `HTTP ${res.status}`;
    }
};

export const getDeliverySettings = async (
    tenantSlug: string,
    tenantId?: string | null
): Promise<DeliverySettings> => {
    const res = await apiFetch(`/api/public/tenants/${tenantSlug}/delivery/settings`, {
        method: 'GET',
        tenantId: tenantId ?? undefined,
    });

    if (!res.ok) {
        throw new Error(await readErrorMessage(res));
    }

    const data = await res.json();
    return mapSettings(data);
};

export const createDeliveryOrder = async (
    tenantSlug: string,
    payload: DeliveryOrderRequestPayload,
    tenantId?: string | null,
    authToken?: string | null
): Promise<DeliveryOrderResponse> => {
    const res = await apiFetch(`/api/public/tenants/${tenantSlug}/delivery/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        tenantId: tenantId ?? undefined,
        authToken: authToken ?? undefined,
    });

    if (!res.ok) {
        throw new Error(await readErrorMessage(res));
    }

    const data = await res.json();
    return mapOrderResponse(data);
};

export interface PayPalPublicConfig {
    clientId: string;
    currency: string;
}

const mapPayPalConfig = (raw: any): PayPalPublicConfig => {
    const clientId = typeof raw?.clientId === 'string' ? raw.clientId.trim() : '';
    const currency = typeof raw?.currency === 'string' ? raw.currency.trim() : 'EUR';
    return {
        clientId,
        currency: currency || 'EUR',
    };
};

export const getPayPalPublicConfig = async (
    tenantSlug: string,
    tenantId?: string | null
): Promise<PayPalPublicConfig> => {
    const res = await apiFetch(`/api/public/tenants/${tenantSlug}/delivery/payments/paypal/config`, {
        method: 'GET',
        tenantId: tenantId ?? undefined,
    });

    if (!res.ok) {
        throw new Error(await readErrorMessage(res));
    }

    const data = await res.json();
    return mapPayPalConfig(data);
};
